import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import { tabbable } from "tabbable";
import { useStringMap } from "../../utilities/string-map/useStringMap";
import { useIntersectionObserver } from "../../hooks/useIntersectionObserver";
import { useWhatInput } from "../../hooks/useWhatInput";
import { FocusList } from "../../utilities/focus-list/FocusList";
import { Button } from "../../button/Button";
import { stringMapDefault } from "../multi-select/MultiSelect.stringMap";
import { SelectSearchFilter } from "./SelectSearchFilter";
import * as Styled from "./Select.styled";
import { BottomAnchor } from "./SelectMenu.styled";
const DEFAULT_SEARCH_DEBOUNCE = 150;
const renderDefaultSearchInput = (props) => _jsx(SelectSearchFilter, { ...props });
export const SelectMenu = forwardRef((props, ref) => {
    /* istanbul ignore next */
    const { onApply, "aria-labelledby": ariaLabelledBy, applyProps, cancelProps, children, closeAndReturnFocus, defaultSearchValue = "", hasReachedSelectedLimit, hasCheckboxes, isFullWidth, isLoading, isMulti, isSearchable, loadingProps, noOptionsMessage, noSearchResultsMessage, maxHeight, onCancel, onMenuClose, onMenuOpen, onMenuScroll, onMenuScrollEnd, onSearchFilter, onUpdate, options, renderSearchInput = renderDefaultSearchInput, renderNestedGroupLabel, searchProps, selectedOption, selectedOptions, selectedValue, shouldAutoFocusMenu, shouldClearAncestorsOnSearch, stringMap: stringMapProp, toggleRef, overlayRef, ...otherProps } = props;
    // TODO: create better API for subcomponents, SelectMenu is shared between Select and MultiSelect, which stringMap should it use?
    const stringMap = useStringMap("MultiSelect", { stringMapDefault, stringMapProp });
    const hasFooter = Boolean(onApply);
    // State
    const [searchText, setSearchText] = useState(defaultSearchValue);
    const [ancestorOptions, setAncestorOptions] = useState([]);
    const [currentOptions, setCurrentOptions] = useState();
    const [depth, setDepth] = useState(0);
    const [optionsHistoryState, setOptionsHistoryState] = useState([]);
    const isTouch = useWhatInput().inputType === "touch";
    // Refs
    const listWrapperRef = useRef(null);
    const listRef = useRef(null);
    const selectedElementsRef = useRef(selectedOptions);
    useEffect(() => {
        onMenuOpen && onMenuOpen();
        return () => onMenuClose && onMenuClose();
        // Only run on mount, even if the callbacks change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Callbacks
    const handlePush = (nextAncestor, nextOptions, prevOptions = []) => {
        const newAncestors = [...ancestorOptions, nextAncestor];
        const newHistory = optionsHistoryState ? [...optionsHistoryState, prevOptions] : false;
        setDepth((prevDepth) => prevDepth + 1);
        // TODO: refactor using useReducer
        setSearchText("");
        setAncestorOptions(newAncestors);
        setCurrentOptions(nextOptions);
        if (newHistory) {
            setOptionsHistoryState(newHistory);
        }
    };
    const handleBack = () => {
        setDepth((prevDepth) => prevDepth - 1);
        const newAncestors = [...ancestorOptions];
        newAncestors.pop();
        setSearchText("");
        setAncestorOptions(newAncestors);
        const newHistory = optionsHistoryState ? [...optionsHistoryState] : false;
        if (newHistory && optionsHistoryState && optionsHistoryState[depth - 1]) {
            newHistory.pop();
            setOptionsHistoryState(newHistory);
            setCurrentOptions(optionsHistoryState[depth - 1]);
        }
    };
    const clearAncestorOptions = () => setAncestorOptions([]);
    const debounceDuration = searchProps && searchProps.debounceDuration ? searchProps.debounceDuration : DEFAULT_SEARCH_DEBOUNCE;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- maybe debounce obfuscates the functional internals?
    const handleSearchFilter = useCallback(debounce((newSearchText) => {
        if (ancestorOptions && shouldClearAncestorsOnSearch) {
            clearAncestorOptions();
        }
        onSearchFilter && onSearchFilter(newSearchText);
    }, debounceDuration), [ancestorOptions, debounceDuration, onSearchFilter, shouldClearAncestorsOnSearch]);
    const handleSearchInputChange = (event) => {
        if (event && event.target) {
            setSearchText(event.target.value);
            handleSearchFilter(event.target.value);
        }
    };
    const bottomAnchorRef = useRef(null);
    const scrollState = useRef({
        hasScrolledBottom: false,
        hasNotifiedOfScrollEnd: false
    });
    useIntersectionObserver({
        targetRef: bottomAnchorRef,
        callback: useCallback((entries) => {
            // will be called when at least 1% of the 1px element becomes visible
            // and then when it's completely invisible
            scrollState.current = {
                hasScrolledBottom: entries[0].isIntersecting,
                hasNotifiedOfScrollEnd: false
            };
        }, []),
        options: useMemo(() => ({
            threshold: 0,
            root: listWrapperRef
        }), [])
    });
    const handleMenuScroll = useCallback(() => {
        const menu = listRef.current;
        const wrapper = listWrapperRef.current;
        if (menu && wrapper) {
            const hasScrolledBottom = scrollState.current.hasScrolledBottom && !scrollState.current.hasNotifiedOfScrollEnd;
            onMenuScroll?.({
                listRef: menu,
                listWrapperRef: wrapper,
                hasScrolledBottom
            });
            if (hasScrolledBottom) {
                onMenuScrollEnd?.();
                scrollState.current.hasNotifiedOfScrollEnd = true;
            }
        }
    }, [onMenuScroll, onMenuScrollEnd]);
    // used to avoid react-hooks/exhaustive-deps
    const handleMenuScrollDebounced = useMemo(() => (onMenuScroll || onMenuScrollEnd) && debounce(handleMenuScroll, 60), [handleMenuScroll, onMenuScroll, onMenuScrollEnd]);
    useEffect(() => {
        // EEQ-82855 call the scroll handler once in case all available options are already displayed without a scrollbar
        if (handleMenuScrollDebounced && listWrapperRef.current?.offsetHeight === listWrapperRef.current?.scrollHeight) {
            handleMenuScrollDebounced();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const itemSelector = `[data-select-item]:not(:disabled):not([aria-disabled="true"])${hasCheckboxes ? `, button:not(:disabled)` : ""}`;
    const handleMenuFallbackFocus = useCallback((event) => {
        if (event.target !== listRef.current || hasCheckboxes) {
            // Only do this when focus event is caused by a click or in a mode where no listbox element is present
            return;
        }
        // FocusList will set tabIndex={0} to the currently focused item,
        // so unless the developer overrides this it should be the one we want to focus
        // (but we check for itemSelector just in case)
        const lastFocusedItem = tabbable(listRef.current).find((item) => item.matches(itemSelector));
        if (document.activeElement === lastFocusedItem) {
            lastFocusedItem?.blur(); // ensure that keyboard focus is actually
        }
        // Immediate focus() or requestAnimationFrame won't announce the change of the focused element
        setTimeout(() => lastFocusedItem?.focus(), 300);
    }, [hasCheckboxes, itemSelector]);
    const parentOption = ancestorOptions[ancestorOptions.length - 1];
    return (_jsxs(_Fragment, { children: [_jsxs(FocusList, { ref: ref, itemSelector: itemSelector, groupingSelector: "[data-select-item-group]", focusManagementStrategy: hasCheckboxes ? "tabOrder" : "rovingTabIndex", shouldAutoFocus: shouldAutoFocusMenu, preventScrollOnAutoFocus: isTouch, shouldCirculate: false, shouldFocusMatchOnType: !isSearchable, "data-testid": "SelectMenu", ...otherProps, children: [isSearchable &&
                        renderSearchInput({
                            value: searchText,
                            isLoading,
                            isFirst: !hasCheckboxes,
                            options,
                            onChange: handleSearchInputChange,
                            ...searchProps
                        }), _jsxs(Styled.MenuListWrapper, { ref: listWrapperRef, isSearchable: isSearchable, hasCheckboxes: hasCheckboxes, hasFooter: hasFooter, onScroll: handleMenuScrollDebounced, style: {
                            maxHeight: maxHeight || "40vh"
                        }, "data-testid": "SelectMenuListWrapper", children: [_jsx(Styled.MenuList, { ref: listRef, 
                                // using a real fieldset tag produces weird results with Chrome (see DES-3980), so we use role="group" for now
                                // TODO: change to fieldset, add "N of N items" legend for isSearchable and make legend live - DES-3351
                                role: hasCheckboxes ? "group" : "listbox", "aria-labelledby": [...(parentOption ? [`${parentOption.parentId}-label`] : []), ariaLabelledBy]
                                    .filter(Boolean)
                                    .join(" "), "aria-multiselectable": hasCheckboxes ? undefined : isMulti, tabIndex: -1, onFocus: handleMenuFallbackFocus, children: children({
                                    ancestorOptions,
                                    back: handleBack,
                                    clearAncestorOptions,
                                    clearSearchText: () => setSearchText(""),
                                    closeAndReturnFocus,
                                    currentOptions,
                                    depth,
                                    hasReachedSelectedLimit,
                                    isLoading,
                                    listRef,
                                    loadingProps,
                                    noOptionsMessage,
                                    noSearchResultsMessage,
                                    onUpdate,
                                    options,
                                    overlayRef,
                                    parentOption,
                                    push: handlePush,
                                    renderNestedGroupLabel,
                                    searchText,
                                    selectedValue,
                                    selectedOptions,
                                    selectedOption,
                                    stringMap: stringMapProp
                                }) }, parentOption?.parentId ?? "root"), _jsx(BottomAnchor, { ref: bottomAnchorRef })] })] }), hasFooter && (_jsxs(Styled.ApplyContainer, { "data-testid": "SelectMenuApplyContainer", children: [_jsx(Button, { priority: "secondary", onClick: () => {
                            onCancel && onCancel(selectedElementsRef.current);
                            closeAndReturnFocus();
                        }, ...cancelProps, children: stringMap?.cancel }), _jsx(Button, { disabled: isEqual(selectedOptions?.map((v) => v.value), selectedElementsRef.current?.map((v) => v.value)), onClick: () => {
                            onApply?.(selectedOptions);
                            closeAndReturnFocus && closeAndReturnFocus();
                        }, ...applyProps, children: stringMap?.apply })] }))] }));
});
SelectMenu.displayName = "SelectMenu";
SelectMenu.defaultProps = {
    renderSearchInput: renderDefaultSearchInput,
    shouldAutoFocusMenu: true,
    defaultSearchValue: "",
    keyboardDirection: "vertical"
};
